/**
 * /tools — public free-tool hub. SEO long-tail magnet:
 *   • 6 tools × 26 langs = 156 indexed pages
 *   • Each card links to /tools/:slug
 *   • SOON cards open a waitlist modal (lead-capture for upcoming tools)
 *   • Embed mode (?embed=1) strips chrome
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Sparkles, Lock, X, Loader2, CheckCircle2, Bell } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { API } from "../config";
import { getHubT } from "../translations-tools";

const ORDER = [
  { slug: "backlink-checker", live: true, accent: "#a855f7" },
  { slug: "ai-visibility-checker", live: true, accent: "#10B981" },
  { slug: "seo-grade", live: false, accent: "#F59E0B" },
  { slug: "competitor-analyzer", live: false, accent: "#EF4444" },
  { slug: "geo-audit", live: false, accent: "#3B82F6" },
  { slug: "page-speed", live: false, accent: "#EC4899" },
];

export default function ToolsHubPage() {
  const { language } = useLanguage();
  const [params] = useSearchParams();
  const t = getHubT(language || "en");
  const embed = params.get("embed") === "1";
  const [waitlistFor, setWaitlistFor] = useState(null);

  useEffect(() => {
    document.title = `${t.hero_h1} · TopRankPulse`;
  }, [t.hero_h1]);

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-white" data-testid="tools-hub-page">
      {!embed && (
        <header className="border-b border-[var(--color-border)] py-4 px-6">
          <Link to="/" className="font-black text-lg font-['Cabinet_Grotesk']">TopRankPulse</Link>
        </header>
      )}

      <section className="max-w-6xl mx-auto px-4 py-16 text-center">
        <p className="text-xs font-mono tracking-[0.3em] text-[#a855f7] mb-4" data-testid="tools-hub-eyebrow">
          {t.hero_eyebrow}
        </p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Cabinet_Grotesk'] mb-6 leading-tight">
          {t.hero_h1}
        </h1>
        <p className="text-base sm:text-lg text-[var(--color-text-muted)] max-w-2xl mx-auto mb-12">{t.hero_sub}</p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {ORDER.map((tool) => {
            const meta = t.tools[tool.slug] || { name: tool.slug, desc: "", tag: "" };
            return (
              <ToolCard
                key={tool.slug}
                slug={tool.slug}
                meta={meta}
                live={tool.live}
                accent={tool.accent}
                t={t}
                onWaitlist={() => setWaitlistFor({ slug: tool.slug, name: meta.name, accent: tool.accent })}
              />
            );
          })}
        </div>
      </section>

      {waitlistFor && (
        <WaitlistModal
          tool={waitlistFor}
          language={language || "en"}
          t={t}
          onClose={() => setWaitlistFor(null)}
        />
      )}
    </div>
  );
}

function ToolCard({ slug, meta, live, accent, t, onWaitlist }) {
  const inner = (
    <div
      className={`relative group p-6 border-2 text-left transition-all h-full ${
        live
          ? "border-[var(--color-border)] bg-[var(--color-surface)] hover:border-[var(--color-text)] cursor-pointer"
          : "border-dashed border-[var(--color-border)] bg-[var(--color-bg)]/40 hover:border-[var(--color-text)]/50 cursor-pointer"
      }`}
      data-testid={`tool-card-${slug}`}
    >
      <div className="flex items-start justify-between mb-3">
        <span
          className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded ${
            live ? "text-black" : "bg-[var(--color-surface-alt)] text-[var(--color-text-muted)]"
          }`}
          style={live ? { background: accent } : {}}
        >
          {live ? t.card_status_live : t.card_status_soon}
        </span>
        {meta.tag && (
          <span className="text-[9px] font-black uppercase text-[#FFE600]">{meta.tag}</span>
        )}
      </div>
      <h3 className="text-xl font-black font-['Cabinet_Grotesk'] mb-2">{meta.name}</h3>
      <p className="text-sm text-[var(--color-text-muted)] mb-4">{meta.desc}</p>
      <div className="flex items-center gap-2 text-sm font-bold" style={{ color: live ? accent : "var(--color-text-muted)" }}>
        {live ? (
          <>
            {t.cta_run} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </>
        ) : (
          <>
            <Bell className="w-3.5 h-3.5" />
            {t.wait_cta}
          </>
        )}
      </div>
    </div>
  );
  if (!live) {
    return (
      <button
        type="button"
        onClick={onWaitlist}
        className="text-left"
        data-testid={`tool-card-${slug}-waitlist-trigger`}
      >
        {inner}
      </button>
    );
  }
  return <Link to={`/tools/${slug}`}>{inner}</Link>;
}

function WaitlistModal({ tool, language, t, onClose }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | ok | err
  const [err, setErr] = useState("");
  const [count, setCount] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/funnel-tools/waitlist-count`, { params: { slug: tool.slug } });
        if (!cancelled) setCount(r.data?.waitlist_count ?? 0);
      } catch {
        /* social proof is optional */
      }
    })();
    return () => { cancelled = true; };
  }, [tool.slug]);

  const submit = async (e) => {
    e.preventDefault();
    const clean = email.trim();
    if (!clean || !/^\S+@\S+\.\S+$/.test(clean)) {
      setErr(t.wait_err_email);
      return;
    }
    setState("loading"); setErr("");
    try {
      const r = await axios.post(`${API}/funnel-tools/waitlist`, {
        tool_slug: tool.slug,
        email: clean,
        language,
      });
      setCount(r.data?.waitlist_count ?? count);
      setState("ok");
    } catch (e2) {
      const code = e2?.response?.data?.detail;
      setErr(code === "invalid_email" ? t.wait_err_email : t.wait_err);
      setState("err");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="waitlist-modal"
    >
      <div
        className="relative w-full max-w-md bg-[var(--color-surface)] border-2 border-[var(--color-border)] p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-[var(--color-text-muted)] hover:text-white"
          aria-label={t.wait_close}
          data-testid="waitlist-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-2 mb-3">
          <span
            className="text-[10px] font-black uppercase tracking-wider px-2 py-1"
            style={{ background: tool.accent, color: "#000" }}
          >
            {t.card_status_soon}
          </span>
          <span className="text-sm font-bold">{tool.name}</span>
        </div>

        <h3 className="text-2xl font-black font-['Cabinet_Grotesk'] mb-2 leading-tight">
          {t.wait_title}
        </h3>
        <p className="text-sm text-[var(--color-text-muted)] mb-5">{t.wait_sub}</p>

        {state !== "ok" ? (
          <form onSubmit={submit} className="space-y-3" data-testid="waitlist-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t.wait_placeholder}
              className="w-full px-4 py-3 bg-[var(--color-bg)] border-2 border-[var(--color-border)] text-white text-base focus:border-white focus:outline-none"
              data-testid="waitlist-email-input"
              disabled={state === "loading"}
              autoFocus
            />
            {err && (
              <p className="text-xs text-red-400" data-testid="waitlist-error">{err}</p>
            )}
            <button
              type="submit"
              disabled={state === "loading"}
              className="w-full px-6 py-3 font-black uppercase tracking-wider text-black transition-opacity disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ background: tool.accent }}
              data-testid="waitlist-submit"
            >
              {state === "loading" ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> {t.wait_btn_loading}</>
              ) : (
                t.wait_btn
              )}
            </button>
            {count !== null && count > 0 && (
              <p className="text-xs text-[var(--color-text-muted)] text-center" data-testid="waitlist-count">
                {t.wait_count.replace("{n}", count)}
              </p>
            )}
          </form>
        ) : (
          <div className="text-center py-4" data-testid="waitlist-success">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-3" style={{ color: tool.accent }} />
            <p className="text-base font-bold mb-4">{t.wait_success}</p>
            <button
              type="button"
              onClick={onClose}
              className="text-sm text-[var(--color-text-muted)] hover:text-white underline"
              data-testid="waitlist-success-close"
            >
              {t.wait_close}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
